import { PdfRecoveryNotice } from "../components/PdfRecoveryNotice";
import { SourceCard, displayLink } from "../components/SourceCard";
import { formatVideoDuration } from "../lib/formatVideoDuration";
import { handoffToolUrl, WebsiteHandoffError } from "../lib/websiteHandoff";
import { describeSourceInput, incomingLink, isLink, linkInText, splitLinksFromInstructions, withLinkScheme } from "../lib/sourceLink";
import { WebsiteHandoffRecovery } from "../components/WebsiteHandoffRecovery";
import { clearCreationIntent, keepsTextAsCaption } from "../lib/onboardingIntent";
import { GenerationSettings } from "../components/GenerationSettings";
import { ExpandingWorkspace } from "../components/autopilot/ExpandingWorkspace";
import {
  FileText,
  Link as LinkIcon,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import posthog from "posthog-js";
import {
  appendPendingRequest,
  generationFailurePatch,
  composerForTextFallback,
  readPendingRequests,
  GenerationRetry,
  generationRequestDocId,
  PendingSource,
  removePendingRequest,
  updatePendingRequest,
} from "../lib/pendingGeneration";
import { getGenerationQualityVariant } from "../lib/generationQuality";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import {
  DEFAULT_VISUAL_STYLE_ID,
  brandKitImagesDisabledReason,
  VisualStyleId,
} from "../constants/visualStyles";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useWorkspaceBrandKits } from "../hooks/useWorkspaceBrandKits";
import { cn } from "../lib/utils";
import { db } from "../services/Firebase";
import { GenerateWorkingState } from "../components/GenerateWorkingState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { useToast } from "../components/ui/use-toast";
import { useUpgradePage } from "../hooks/useUpgradePage";

type Source = {
  id: string;
  documentId?: string;
  validationId?: string;
  type: "url" | "text";
  value: string;
  title?: string;
  favicon?: string;
  error?: string;
  /** Set by validate-urls for source-specific metadata. */
  kind?: "youtube" | "article" | "pdf";
  channel?: string;
  thumbnailUrl?: string;
  durationSec?: number;
  /** The link a text source was read from (an Instagram profile's captions). */
  fromLink?: string;
};

type ValidateUrlResultDto = {
  documentId?: string;
  canonicalUrl?: string;
  url: string;
  ok: boolean;
  title?: string;
  favicon?: string;
  error?: string;
  kind?: "youtube" | "article" | "pdf";
  channel?: string;
  thumbnailUrl?: string;
  durationSec?: number;
  /** Use this text in place of the link (an Instagram profile's bio and captions). */
  sourceText?: string;
};

/**
 * A checked link as a source. An Instagram profile becomes its bio and
 * captions as pasted text, so people see (and can edit) what posts come from.
 */
function sourceFromValidation(result: ValidateUrlResultDto, base: Pick<Source, "id"> & Partial<Source>): Source {
  if (result.sourceText)
    return { id: base.id, validationId: base.validationId, type: "text", value: result.sourceText, title: result.title, fromLink: result.url };
  return {
    ...base,
    documentId: result.documentId,
    type: "url",
    value: result.canonicalUrl || base.value || result.url,
    title: result.title,
    favicon: result.favicon,
    kind: result.kind,
    channel: result.channel,
    thumbnailUrl: result.thumbnailUrl,
    durationSec: result.durationSec,
  };
}

type InitiatedPost = { postId: string; title?: string };
type GenerationConfirmationResult = {
  posts: InitiatedPost[];
  status?: "empty" | "failed" | "timeout";
};
const GENERATION_CONFIRMATION_TIMEOUT_MS = 180_000;

// generationRequestDocId now lives in lib/pendingGeneration (shared with the
// gallery's pending tiles).

// Known-good article used by the empty-state "Try an example →" link.
// James Clear essays parse cleanly and are broadly relevant to expertise-led
// businesses (coaches, consultants, service pros, SaaS).
const EXAMPLE_URL = "https://jamesclear.com/continuous-improvement";

const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

function parsedInputUrl(value: string): URL | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    );
  } catch {
    return null;
  }
}

function isYoutubeHostValue(value: string): boolean {
  const url = parsedInputUrl(value);
  if (!url) return false;
  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  return (
    hostname === "youtu.be" ||
    hostname === "youtube.com" ||
    hostname.endsWith(".youtube.com")
  );
}

function isYoutubeUrlValue(value: string): boolean {
  const url = parsedInputUrl(value);
  if (!url) return false;
  const hostname = url.hostname
    .toLowerCase()
    .replace(/^(www\.|m\.|music\.)/, "");

  if (hostname === "youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0];
    return !!id && YOUTUBE_VIDEO_ID_PATTERN.test(id);
  }

  if (hostname !== "youtube.com") return false;
  const segments = url.pathname.split("/").filter(Boolean);
  if (segments[0] === "watch") {
    const id = url.searchParams.get("v");
    return !!id && YOUTUBE_VIDEO_ID_PATTERN.test(id);
  }
  if (["shorts", "live", "embed"].includes(segments[0] || "") && segments[1]) {
    return YOUTUBE_VIDEO_ID_PATTERN.test(segments[1]);
  }
  return false;
}

const isLocalFailureInput = (value: string) => {
  if (process.env.NODE_ENV !== "development") return false;
  if (value.trim().startsWith("[LOCAL GENERATION FAILURE]")) return true;
  try {
    return new URL(value.trim()).hash === "#local-generation-failure";
  } catch {
    return false;
  }
};

export default function GeneratePage({
  embedded = false,
  inline = false,
  initialIntent,
  sourceHeading,
  submitLabel = "Make my posts",
  postsPerSource,
  onSubmitted,
  showManageBrandKits = true,
  initialBrandKitId,
}: {
  embedded?: boolean;
  /** The full configuration stays open on the page instead of in an overlay. */
  inline?: boolean;
  /**
   * Starting content as a query string (articleUrl, inputText, templateId…),
   * read instead of the page address and never written back to it, so a host
   * page such as onboarding keeps its own address.
   */
  initialIntent?: string;
  /** Replaces "Source added" and hides the source count. */
  sourceHeading?: string;
  submitLabel?: string;
  /**
   * Asks for this many posts per source (the prompt returns fewer when the
   * source can't support them) and hides the cap control.
   */
  postsPerSource?: number;
  /** Runs instead of opening Drafts once posts are requested. */
  onSubmitted?: () => void;
  /** Off where leaving for Brand Kits would interrupt a flow, such as onboarding. */
  showManageBrandKits?: boolean;
  /**
   * Starts with this kit selected, for a host page such as onboarding that
   * just made it, so the first post never depends on the kit list arriving
   * before the user submits.
   */
  initialBrandKitId?: string;
}) {
  const { currentUser, customerData, refreshCustomerData, activeWorkspaceId } =
    useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [intentParams, setIntentParams] = useState(() => new URLSearchParams(initialIntent || ""));
  const usesIntent = initialIntent !== undefined;
  const searchParams = usesIntent ? intentParams : urlParams;
  const setSearchParams = useMemo(
    () => usesIntent
      ? ((next: URLSearchParams | ((current: URLSearchParams) => URLSearchParams)) =>
          setIntentParams((current) => new URLSearchParams(typeof next === "function" ? next(new URLSearchParams(current)) : next))) as typeof setUrlParams
      : setUrlParams,
    [usesIntent, setUrlParams],
  );
  const fromOnboarding = searchParams.get("setup") === "ready";
  // With the configuration always visible, incoming content goes straight
  // into the source list rather than the input box.
  const sourcesFirst = fromOnboarding || inline;
  const [configurationOpenState, setConfigurationOpen] = useState(fromOnboarding);
  const configurationOpen = inline || configurationOpenState;
  useEffect(() => {
    if (fromOnboarding) setConfigurationOpen(true);
  }, [fromOnboarding]);
  const { availableCreditPacks, currentPlan, isFreePlan } = useStripe();
  const sourcesStorageKey = activeWorkspaceId
    ? `generate_sources_${activeWorkspaceId}`
    : null;
  const smartBarStorageKey = activeWorkspaceId
    ? `generate_smartbar_${activeWorkspaceId}`
    : null;

  const articleUrlParam = searchParams.get("articleUrl");
  const inputTextParam = searchParams.get("inputText") || "";
  const initialSource = searchParams.get("source");
  const initialPreserveInputAsCaption = keepsTextAsCaption(searchParams);
  // A link handed over with a request typed around it brings that request
  // along, whether it came as a link or as text from a tool page.
  const linkInInputText =
    !articleUrlParam && !initialPreserveInputAsCaption ? linkInText(inputTextParam) : null;
  const incomingArticle = articleUrlParam ? incomingLink(articleUrlParam) : linkInInputText;
  const initialArticleUrl = incomingArticle?.link || null;
  const initialInstructions =
    searchParams.get("instructions") || incomingArticle?.instructions || "";
  const initialInputText = linkInInputText ? "" : inputTextParam;

  // Build initial sources from query params (backward compat)
  const initialSources: Source[] = [];
  if (initialArticleUrl)
    initialSources.push({
      id: crypto.randomUUID(),
      type: "url",
      value: initialArticleUrl,
    });
  if (initialInputText)
    initialSources.push({
      id: crypto.randomUUID(),
      type: "text",
      value: initialInputText,
    });

  const linkedInput = embedded && !sourcesFirst && initialSources.length === 1
    ? initialSources[0].value
    : null;

  // Restore sources from sessionStorage if no query params provided, so state survives navigation
  const [sources, setSources] = useState<Source[]>(() => {
    if (initialSources.length > 0) {
      return linkedInput !== null ? [] : initialSources;
    }
    try {
      const saved = sourcesStorageKey
        ? sessionStorage.getItem(sourcesStorageKey)
        : null;
      const restored = saved ? (JSON.parse(saved) as Source[]) : [];
      return restored;
    } catch {
      return [];
    }
  });
  const [generationMode, setGenerationMode] = useState<"batch" | "synthesis">(
    "batch",
  );
  // What the person asks of this generation (contentIntent): topic, tone or design.
  const [instructions, setInstructions] = useState(initialInstructions);
  const [postsPerSourceCap, setPostsPerSourceCap] = useState<string>(postsPerSource ? String(postsPerSource) : "");
  const [showPostsCap, setShowPostsCap] = useState(false);

  // Smart bar state
  const [smartBarValue, setSmartBarValue] = useState(() => {
    if (linkedInput !== null) return linkedInput;
    if (initialSources.length > 0) return "";
    try {
      return smartBarStorageKey
        ? sessionStorage.getItem(smartBarStorageKey) || ""
        : "";
    } catch {
      return "";
    }
  });
  const [handoffFailed, setHandoffFailed] = useState(false);
  const [smartBarError, setSmartBarError] = useState("");
  const inputEdited = useRef(false);
  const [isValidatingUrls, setIsValidatingUrls] = useState(false);
  const [smartBarFocused, setSmartBarFocused] = useState(false);
  const [addingSource, setAddingSource] = useState(false);
  const addSourceButtonRef = useRef<HTMLButtonElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const smartBarRef = useRef<HTMLDivElement>(null);
  const sourceSubmissionInFlight = useRef(false);

  const [preserveInputAsCaption, setPreserveInputAsCaption] = useState(
    initialPreserveInputAsCaption,
  );
  const [targetLanguage, setTargetLanguage] = useState<string>("auto");
  const [selectedVisualStyleId, setSelectedVisualStyleId] =
    useState<VisualStyleId>(DEFAULT_VISUAL_STYLE_ID);
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<string | null>(
    initialBrandKitId || null,
  );
  const [selectedTemplateLabel, setSelectedTemplateLabel] = useState("");
  const initialTemplateId = searchParams.get("templateId");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    initialTemplateId ? new Set([initialTemplateId]) : new Set(),
  );
  const consumedInitialLink = useRef(false);
  useEffect(() => {
    const hasContent = Boolean(initialArticleUrl || initialInputText);
    if (consumedInitialLink.current && hasContent) {
      const incoming: Source[] = [];
      if (initialArticleUrl) incoming.push({ id: crypto.randomUUID(), type: "url", value: initialArticleUrl });
      if (initialInputText) incoming.push({ id: crypto.randomUUID(), type: "text", value: initialInputText });
      setSources(!sourcesFirst && incoming.length === 1 ? [] : incoming);
      setSmartBarValue(!sourcesFirst && incoming.length === 1 ? incoming[0].value : "");
      setPreserveInputAsCaption(initialPreserveInputAsCaption);
      if (initialInstructions) setInstructions(initialInstructions);
      setSmartBarError("");
      setConfigurationOpen(fromOnboarding);
      inputEdited.current = false;
    }
    consumedInitialLink.current = true;
    if (initialTemplateId) setSelectedTemplateIds(new Set([initialTemplateId]));
    if (!hasContent) return;
    setSearchParams((current) => {
      const cleaned = new URLSearchParams(current);
      cleaned.delete("articleUrl");
      cleaned.delete("inputText");
      cleaned.delete("instructions");
      return cleaned;
    }, { replace: true });
  }, [initialArticleUrl, initialInputText, initialInstructions, initialTemplateId, initialPreserveInputAsCaption, setSearchParams, fromOnboarding, sourcesFirst]);

  const [isGenerating, setIsGenerating] = useState(false);
  const retryingRequestId = useRef<string | null>(null);
  const [pdfRecovery, setPdfRecovery] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [failureTitle, setFailureTitle] = useState("We couldn’t start your posts.");
  const [failureFallback, setFailureFallback] = useState<{ url: string; label: string } | null>(null);
  const clearConfigurationError = useCallback(() => {
    setFailureMessage("");
    setFailureTitle("We couldn’t start your posts.");
    setFailureFallback(null);
  }, []);
  // Instructions are kept under their old name, synthesisPrompt, so saved retries still restore.
  const captureComposer = (): GenerationRetry => ({ sources, generationMode, synthesisPrompt: instructions,
    postsPerSourceCap, preserveInputAsCaption, targetLanguage, selectedVisualStyleId,
    // Accounts are chosen when publishing, not when generating.
    selectedBrandKitId, selectedConnectionIds: [], templateIds: Array.from(selectedTemplateIds) });
  const parkedComposer = useRef<{ config: GenerationRetry; input: string } | null>(null);
  const restoreComposer = useCallback((saved: GenerationRetry) => {
    setSources(saved.sources); setGenerationMode(saved.generationMode);
    setInstructions(saved.synthesisPrompt || ""); setPostsPerSourceCap(saved.postsPerSourceCap);
    setPreserveInputAsCaption(saved.preserveInputAsCaption); setTargetLanguage(saved.targetLanguage);
    setSelectedVisualStyleId(saved.selectedVisualStyleId as VisualStyleId);
    setSelectedBrandKitId(saved.selectedBrandKitId);
    setSelectedTemplateIds(new Set(saved.templateIds)); setSmartBarError("");
    clearConfigurationError();
  }, [clearConfigurationError]);
  const retryRequestId = searchParams.get("retryRequest");
  const handledRetry = useRef<string | null>(null);
  useEffect(() => {
    if (!retryRequestId) { handledRetry.current = null; return; }
    if (isGenerating) return;
    if (handledRetry.current === retryRequestId) return;
    handledRetry.current = retryRequestId;
    const request = readPendingRequests(activeWorkspaceId).find((item) => item.clientRequestId === retryRequestId);
    if (request?.error && request.retryData) {
      parkedComposer.current = { config: captureComposer(), input: smartBarValue };
      retryingRequestId.current = retryRequestId;
      const unsupportedPdf = request.errorCode === "pdf-unreadable" ||
        (request.fallback?.type === "paste_text" && request.retryData.sources.some(source => source.value === request.fallback?.url && (source.documentId || /\.pdf(?:[?#]|$)/i.test(source.value))));
      const pasteFallback = searchParams.get("recovery") === "paste" || unsupportedPdf ? request.fallback : undefined;
      restoreComposer(composerForTextFallback(request.retryData, pasteFallback, unsupportedPdf));
      setSmartBarValue(""); setConfigurationOpen(true);
      setPdfRecovery(!!unsupportedPdf);
      if (unsupportedPdf) setAddingSource(true);
      if (pasteFallback || unsupportedPdf) {
        setSmartBarFocused(true);
        requestAnimationFrame(() => textAreaRef.current?.focus());
      }
    }
    setSearchParams((current) => { const next = new URLSearchParams(current); next.delete("retryRequest"); next.delete("recovery"); return next; }, { replace: true });
    // Capture the current composer only when a new retry link is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryRequestId, activeWorkspaceId, isGenerating]);

  // Errors belong to the submitted content. Metadata/error annotations on the
  // same sources must not dismiss them, but changing the actual input should.
  const generationInputKey = JSON.stringify([
    smartBarValue,
    sources.map(({ type, value }) => [type, value]),
  ]);
  useEffect(() => {
    clearConfigurationError();
  }, [generationInputKey, clearConfigurationError]);
  const recordFailure = useCallback((message: string, title = "We couldn’t start your posts.", requestId?: string) => {
    if (embedded && requestId && activeWorkspaceId) {
      updatePendingRequest(activeWorkspaceId, requestId, { error: message });
      return;
    }
    setFailureMessage(message);
    setFailureTitle(title);
    setFailureFallback(null);
  }, [embedded, activeWorkspaceId]);
  const closeConfiguration = () => {
    setPdfRecovery(false);
    clearConfigurationError();
    setConfigurationOpen(false);
    if (parkedComposer.current) {
      restoreComposer(parkedComposer.current.config); setSmartBarValue(parkedComposer.current.input);
      parkedComposer.current = null; retryingRequestId.current = null;
    }
  };

  const openDraftsAfterClose = useRef(false);
  const configurationHasClosed = useRef(true);
  const openSubmittedDrafts = useCallback(() => {
    if (!openDraftsAfterClose.current || !configurationHasClosed.current) return;
    openDraftsAfterClose.current = false;
    // Drafts shows the request's placeholders and any failure; start at the top.
    navigate("/created", { state: { fromGeneration: true } });
  }, [navigate]);
  const generationInFlightRef = useRef(false);
  const creditCheckoutSessionClaimRef = useRef<{ key: string; promise: Promise<void> } | null>(null);
  const {
    kits: userBrandKits,
    loaded: brandKitsLoaded,
    synced: brandKitsSynced,
  } = useWorkspaceBrandKits(currentUser ? activeWorkspaceId : null);
  // Only submitted sources count. Separate posts need images for every source;
  // combined posts can share images extracted from any source.
  const sourceMayHaveImages =
    sources.length > 0 &&
    (generationMode === "synthesis"
      ? sources.some((source) => source.type === "url")
      : sources.every((source) => source.type === "url"));
  const selectedBrandKit = useMemo(
    () => userBrandKits.find((kit) => kit.id === selectedBrandKitId),
    [selectedBrandKitId, userBrandKits],
  );
  // A selected kit the server says is gone (deleted in another tab, or a
  // restored request's kit) would make generation fail with "not found".
  // Until the list is confirmed, keep the selection: it is still applied.
  const selectedBrandKitMissing =
    !!selectedBrandKitId && !selectedBrandKit && brandKitsSynced;
  useEffect(() => {
    if (selectedBrandKitMissing) setSelectedBrandKitId(null);
  }, [selectedBrandKitMissing]);
  const imageStyleUnavailableReason =
    selectedVisualStyleId === "brandkit-only"
      ? selectedBrandKitId && !brandKitsLoaded
        ? "Your Brand Kit is still loading. Please try again in a moment."
        : brandKitImagesDisabledReason(selectedBrandKit, sourceMayHaveImages)
      : undefined;
  const { navigateToUpgrade } = useUpgradePage();
  const hasActiveSubscription = !!currentPlan?.stripePriceId;
  const smallestCreditPack = useMemo(
    () =>
      hasActiveSubscription
        ? [...availableCreditPacks].sort(
            (a, b) => a.video_credits - b.video_credits,
          )[0]
        : undefined,
    [availableCreditPacks, hasActiveSubscription],
  );

  const videoCredits =
    (customerData?.videoCredits ?? 0) + (customerData?.bonusCredits ?? 0);

  const allSourcesAreText =
    sources.length > 0 && sources.every((s) => s.type === "text");
  const hasYoutubeSource = sources.some(
    (s) =>
      s.type === "url" && (s.kind === "youtube" || isYoutubeUrlValue(s.value)),
  );

  const handleGetMoreCredits = () => {
    navigateToUpgrade({
      reason: "no_credits",
      hash: smallestCreditPack ? "credit-packs" : undefined,
    });
  };

  const creditCheckoutSessionId = searchParams.get("credit_session_id");
  const creditsStatus = searchParams.get("credits");
  const creditReturnContext = useRef({ setSearchParams, refreshCustomerData, toast });
  creditReturnContext.current = { setSearchParams, refreshCustomerData, toast };
  useEffect(() => {
    if (
      creditsStatus !== "success" ||
      !creditCheckoutSessionId ||
      !currentUser
    ) {
      return;
    }
    const claimKey = `${currentUser.uid}:${creditCheckoutSessionId}`;
    // Reuse the request across StrictMode effect replays, but let the active
    // effect finish the confirmation and URL cleanup.
    if (creditCheckoutSessionClaimRef.current?.key !== claimKey) {
      const promise = (async () => {
        const token = await currentUser.getIdToken();
        const apiBase = process.env.REACT_APP_API_BASE || "https://api.wavegen.ai";
        const res = await fetch(`${apiBase}/stripe/fulfill-credit-checkout-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId: creditCheckoutSessionId }),
        });
        if (!res.ok) throw new Error(await res.text());
      })();
      creditCheckoutSessionClaimRef.current = { key: claimKey, promise };
    }
    const claim = creditCheckoutSessionClaimRef.current;
    let cancelled = false;
    (async () => {
      let shouldClearParams = false;
      try {
        await claim.promise;
        shouldClearParams = true;
        if (cancelled) return;
        await creditReturnContext.current.refreshCustomerData();
        if (cancelled) return;
        creditReturnContext.current.toast({
          title: "Credits added",
          description: "Your purchased credits are ready.",
        });
      } catch (err) {
        if (cancelled) return;
        creditCheckoutSessionClaimRef.current = null;
        console.error("Failed to fulfill credit checkout session:", err);
        creditReturnContext.current.toast({
          variant: "destructive",
          title: "Payment received",
          description:
            "We're still syncing your credits. Please refresh in a moment.",
        });
      } finally {
        if (cancelled || !shouldClearParams) return;
        creditReturnContext.current.setSearchParams(current => {
          const cleaned = new URLSearchParams(current);
          cleaned.delete("credits");
          cleaned.delete("credit_session_id");
          return cleaned;
        }, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    creditsStatus,
    creditCheckoutSessionId,
    currentUser,
  ]);

  // Restore either a pre-signup handoff or an owned PDF reopened from a draft.
  const pendingContentParam = searchParams.get("pendingContent");
  const pendingClaimToken = searchParams.get("claimToken");
  const sourceDocumentParam = searchParams.get("sourceDocument");
  const [handoffAttempt, setHandoffAttempt] = useState(0);
  const [handoff, setHandoff] = useState<{ status: "loading" | "error"; message?: string } | null>(null);
  useEffect(() => {
    if ((!pendingContentParam && !sourceDocumentParam) || !currentUser || !activeWorkspaceId) return;
    let cancelled = false;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 30_000);
    setHandoff({ status: "loading" });
    (async () => {
      let failureReason = "network";
      try {
        const token = await currentUser.getIdToken();
        const apiBase = process.env.REACT_APP_API_BASE || "https://api.wavegen.ai";
        const path = sourceDocumentParam
          ? `/api/content/documents/${encodeURIComponent(sourceDocumentParam)}`
          : `/api/content/pending/${encodeURIComponent(pendingContentParam!)}?claimToken=${encodeURIComponent(pendingClaimToken || "")}`;
        const res = await fetch(`${apiBase}${path}`, {
          headers: { Authorization: `Bearer ${token}`, "x-workspace-id": activeWorkspaceId },
          signal: controller.signal,
        });
        if (!res.ok) {
          failureReason = `http_${res.status}`;
          throw new Error(res.status === 410 ? "This upload has expired. Upload your PDF again or paste its text." : "We couldn't restore your input. Try again — you don't need to sign up again.");
        }
        const body = await res.json() as { content?: unknown; source?: string; input?: { type: string; value: string; documentId?: string; title?: string } };
        if (cancelled) return;
        if (body.input?.type === "url" && body.input.documentId && body.input.value) {
          const input = body.input;
          setSources(prev => prev.some(source => source.documentId === input.documentId) ? prev : [...prev, {
            id: crypto.randomUUID(), type: "url", value: input.value, documentId: input.documentId, title: input.title || "Your PDF",
          }]);
        } else if (typeof body.content === "string" && body.content.trim()) {
          const text = body.content;
          if (embedded && !sourcesFirst && !inputEdited.current) setSmartBarValue(text);
          else setSources(prev => prev.some(source => source.type === "text" && source.value === text) ? prev : [...prev, { id: crypto.randomUUID(), type: "text", value: text }]);
          setPreserveInputAsCaption(["linkedin-text-formatter", "instagram-font-generator"].some(source => body.source?.includes(source)));
        } else {
          failureReason = "empty_response";
          throw new Error("We couldn't restore your input. Try again or paste it below.");
        }
        setHandoff(null);
        posthog.capture("content_handoff_restored", { handoff_id: pendingContentParam || sourceDocumentParam, source: body.source, input_type: body.input?.documentId ? "pdf" : "text" });
        const cleaned = new URLSearchParams(searchParams);
        cleaned.delete("pendingContent"); cleaned.delete("claimToken"); cleaned.delete("sourceDocument");
        setSearchParams(cleaned, { replace: true });
      } catch (error) {
        if (cancelled) return;
        if (pendingContentParam) setHandoffFailed(true);
        setHandoff({ status: "error", message: error instanceof Error && error.name !== "AbortError" ? error.message : "Restoring your input took too long. Please try again." });
        posthog.capture("content_handoff_restore_failed", { handoff_id: pendingContentParam || sourceDocumentParam, reason: failureReason });
      } finally { window.clearTimeout(timer); }
    })();
    return () => { cancelled = true; window.clearTimeout(timer); controller.abort(); };
  }, [pendingContentParam, pendingClaimToken, sourceDocumentParam, currentUser, activeWorkspaceId, embedded, sourcesFirst, handoffAttempt, searchParams, setSearchParams]);

  // Persist sources and smart bar value to sessionStorage so they survive navigation
  useEffect(() => {
    try {
      if (sourcesStorageKey) {
        sessionStorage.setItem(sourcesStorageKey, JSON.stringify(sources));
      }
    } catch {}
  }, [sources, sourcesStorageKey]);

  useEffect(() => {
    try {
      if (smartBarStorageKey) {
        sessionStorage.setItem(smartBarStorageKey, smartBarValue);
      }
    } catch {}
  }, [smartBarValue, smartBarStorageKey]);

  useEffect(() => {
    if (fromOnboarding && currentUser && activeWorkspaceId &&
        (sources.length > 0 || smartBarValue.trim())) {
      clearCreationIntent(currentUser.uid, activeWorkspaceId);
    }
  }, [fromOnboarding, currentUser, activeWorkspaceId, sources.length, smartBarValue]);

  // Auto-select when user has exactly one brand kit — only once on initial
  // load, so the user can still intentionally switch back to "None". A kit
  // given by the host counts as that selection.
  const didAutoSelectBrandKit = useRef(Boolean(initialBrandKitId));
  useEffect(() => {
    if (
      !didAutoSelectBrandKit.current &&
      userBrandKits.length === 1 &&
      selectedBrandKitId === null
    ) {
      didAutoSelectBrandKit.current = true;
      setSelectedBrandKitId(userBrandKits[0].id);
    }
  }, [userBrandKits, selectedBrandKitId]);

  // Enrich URL sources that are missing metadata (e.g. from query params)
  useEffect(() => {
    const urlsToEnrich = sources.filter(
      (s) => s.type === "url" && !s.title && !s.favicon,
    );
    if (urlsToEnrich.length === 0 || !currentUser) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/batch/validate-urls`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              urls: urlsToEnrich.map((s) => s.value),
            }),
          },
        );
        if (!res.ok || cancelled) return;

        const data = await res.json();
        const results: ValidateUrlResultDto[] = data.results;

        const resultByUrl = new Map(results.map((r) => [r.url, r]));

        if (cancelled) return;

        setSources((prev) =>
          prev.map((s) => {
            if (s.type !== "url" || s.title || s.favicon) return s;
            const result = resultByUrl.get(s.value);
            if (!result) return s;
            if (result.ok) return sourceFromValidation(result, s);
            return {
              ...s,
              error: result.error || "URL not accessible",
            };
          }),
        );
      } catch {
        // Network failure — can't validate, leave sources as-is
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only run once on mount for initial sources
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Auto-grow the textarea to fit content. Reads value from the DOM so the
  // measurement matches whatever React just committed.
  const adjustTextareaHeight = () => {
    const el = textAreaRef.current;
    if (!el) return;
    if (el.value.trim() === "") {
      // Empty: clear inline height so CSS minHeight (focused/unfocused) takes
      // effect — otherwise a stale inline height keeps the bar tall.
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.max(40, el.scrollHeight)}px`;
  };

  // useLayoutEffect runs after DOM commit but before paint, so users never see
  // a flash of mis-sized textarea between value/focus change and resize.
  useLayoutEffect(() => {
    adjustTextareaHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartBarValue, smartBarFocused]);

  // Detect if the current smart bar value is all URLs
  const smartBarLines = smartBarValue
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const isAllUrls =
    smartBarLines.length > 0 && smartBarLines.every((l) => isLink(l));
  // Links typed with a request around them: the links are sources, the rest instructions.
  const smartBarSplit = isAllUrls ? null : splitLinksFromInstructions(smartBarValue);
  const isMultiLine =
    smartBarValue.includes("\n") || smartBarValue.length > 200;

  // --- Smart Bar: submit (auto-detect URL vs text) ---
  const submitSmartBar = async (
    override?: string,
    { keepFocus = false }: { keepFocus?: boolean } = {},
  ): Promise<Source[]> => {
    if (sourceSubmissionInFlight.current || generationInFlightRef.current) return [];
    sourceSubmissionInFlight.current = true;
    try {
      const added = await addSmartBarSource(override);
      if (added.length) {
        setAddingSource(false);
        // Added because focus left the box: it went somewhere on purpose.
        if (!keepFocus) requestAnimationFrame(() => addSourceButtonRef.current?.focus());
        if (embedded && !generationInFlightRef.current) setConfigurationOpen(true);
      }
      return added;
    } finally {
      sourceSubmissionInFlight.current = false;
    }
  };

  const addSmartBarSource = async (override?: string): Promise<Source[]> => {
    const pending = (override ?? smartBarValue).trim();
    if (!pending) return [];

    if (sources.length >= 10) {
      setSmartBarError("Maximum 10 sources reached.");
      return [];
    }

    const lines = pending
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    posthog.capture("source_input_shape", describeSourceInput(pending));
    const links = lines.every((l) => isLink(l))
      ? { links: lines.map(withLinkScheme), instructions: "" }
      : splitLinksFromInstructions(pending);

    if (links) {
      // Validate URLs via backend
      const existingUrls = new Set(
        sources.flatMap((s) => (s.type === "url" ? [s.value] : s.fromLink ? [s.fromLink] : [])),
      );
      const parsed: string[] = [];
      const dupes: string[] = [];
      for (const url of links.links) {
        try {
          new URL(url);
          if (isYoutubeHostValue(url) && !isYoutubeUrlValue(url)) {
            setSmartBarError(
              "Paste a YouTube video URL, not a channel or playlist.",
            );
            return [];
          }
          if (existingUrls.has(url)) {
            dupes.push(url);
          } else {
            parsed.push(url);
          }
        } catch {
          setSmartBarError(`Invalid URL: ${url}`);
          return [];
        }
      }

      if (parsed.length === 0) {
        setSmartBarError(
          dupes.length === 1
            ? "This URL has already been added."
            : "These URLs have already been added.",
        );
        return [];
      }

      const remaining = 10 - sources.length;
      const validationId = crypto.randomUUID();
      posthog.capture("source_validation_started", { validation_id: validationId, source_count: Math.min(parsed.length, remaining) });
      setIsValidatingUrls(true);
      setSmartBarError("");
      const validationController = new AbortController();
      const validationTimer = window.setTimeout(() => validationController.abort(), 30_000);

      try {
        const token = await currentUser?.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/batch/validate-urls`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ urls: parsed.slice(0, remaining) }),
            signal: validationController.signal,
          },
        );

        if (!res.ok) throw new Error("Validation request failed");

        const data = await res.json();
        const results: ValidateUrlResultDto[] = data.results;

        posthog.capture("source_validation_completed", {
          validation_id: validationId,
          source_count: results.length,
          valid_count: results.filter(result => result.ok).length,
          failed_count: results.filter(result => !result.ok).length,
        });

        const validSources: Source[] = [];
        const errors: string[] = [];

        results.forEach((result) => {
          if (result.ok) {
            validSources.push(sourceFromValidation(result, { id: crypto.randomUUID(), validationId }));
          } else {
            errors.push(result.error || "Could not access this page.");
          }
        });

        if (validSources.length > 0) {
          setSources((prev) => [...prev, ...validSources]);
          if (links.instructions)
            setInstructions((prev) =>
              (prev.trim() ? `${prev.trim()}; ${links.instructions}` : links.instructions).slice(0, 500),
            );
          setSmartBarValue("");
          setSmartBarError("");
          setSmartBarFocused(false);
          textAreaRef.current?.blur();
        }

        if (errors.length > 0) {
          setSmartBarError(errors.join("; "));
        }

        return validSources;
      } catch {
        posthog.capture("source_validation_failed", { validation_id: validationId, reason: validationController.signal.aborted ? "timeout" : "request_failed", source_count: parsed.length });
        setSmartBarError(
          "Could not validate URL. Please check your connection and try again.",
        );
        return [];
      } finally {
        window.clearTimeout(validationTimer);
        setIsValidatingUrls(false);
      }
    } else {
      // Add as text source
      if (pending.length < 100) {
        setSmartBarError(
          "Text too short. Please provide at least 100 characters.",
        );
        return [];
      }
      if (pending.length > 100000) {
        setSmartBarError("Text too long. Please limit to 100,000 characters.");
        return [];
      }
      const newSource: Source = {
        id: crypto.randomUUID(),
        type: "text",
        value: pending,
      };
      setSources((prev) => [...prev, newSource]);
      setSmartBarValue("");
      setSmartBarError("");
      setSmartBarFocused(false);
      textAreaRef.current?.blur();
      return [newSource];
    }
  };

  const removeSource = (id: string) => {
    setSources((prev) => prev.filter((s) => s.id !== id));
  };

  // Pasted text is accepted as soon as focus leaves the box, so it has to stay
  // editable: clicking it puts it back into the box (when the box is free).
  const editTextSource = (source: Source) => {
    if (smartBarValue.trim() || isValidatingUrls) return;
    removeSource(source.id);
    setSmartBarValue(source.value);
    setSmartBarError("");
    setAddingSource(true);
    requestAnimationFrame(() => {
      const el = textAreaRef.current;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    });
  };

  // Leaving the box accepts what's in it, the same as Enter: links are checked,
  // text is added, and anything unusable stays in the box with Enter's error.
  const acceptOnLeave = () => {
    if (!smartBarValue.trim() || isValidatingUrls) return;
    void submitSmartBar(undefined, { keepFocus: true });
  };

  const finishGenerationHandoff = useCallback(
    (posts: InitiatedPost[]) => {
      if (embedded) return;
      const postCount = posts.length;
      navigate(isFreePlan ? `/generate?generated=${postCount}` : "/generate");
    },
    [isFreePlan, navigate, embedded],
  );

  const waitForInitiatedPosts = useCallback(
    async (requestId: string): Promise<GenerationConfirmationResult> => {
      if (!currentUser || !activeWorkspaceId) {
        return { posts: [], status: "timeout" };
      }

      const requestDocId = await generationRequestDocId(
        activeWorkspaceId,
        requestId,
      ).catch((error) => {
        console.error("Failed to identify generation request:", error);
        return null;
      });

      return new Promise<GenerationConfirmationResult>((resolve) => {
        if (!currentUser) {
          resolve({ posts: [], status: "timeout" });
          return;
        }

        let settled = false;
        let unsubscribePosts: (() => void) | undefined;
        let unsubscribeRequest: (() => void) | undefined;
        let timeoutId: number | undefined;
        const settle = (result: GenerationConfirmationResult) => {
          if (settled) return;
          settled = true;
          if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
          }
          unsubscribePosts?.();
          unsubscribeRequest?.();
          resolve(result);
        };

        timeoutId = window.setTimeout(() => {
          settle({ posts: [], status: "timeout" });
        }, GENERATION_CONFIRMATION_TIMEOUT_MS);

        try {
          const postsQuery = query(
            collection(db, "posts"),
            where("userId", "==", activeWorkspaceId),
            where("generationRequestId", "==", requestId),
          );

          unsubscribePosts = onSnapshot(
            postsQuery,
            (snapshot) => {
              if (snapshot.empty) return;
              settle({
                posts: snapshot.docs.map((postDoc) => {
                  const data = postDoc.data() as { title?: string };
                  return { postId: postDoc.id, title: data.title };
                }),
              });
            },
            (error) => {
              console.error(
                "Failed to confirm generation start from Firestore:",
                error,
              );
            },
          );
        } catch (error) {
          console.error("Failed to watch generation start:", error);
        }

        if (!requestDocId) return;

        try {
          const requestRef = doc(db, "generation_requests", requestDocId);
          unsubscribeRequest = onSnapshot(
            requestRef,
            (snapshot) => {
              if (!snapshot.exists()) return;

              const data = snapshot.data() as {
                status?: string;
                initiatedPosts?: InitiatedPost[];
              };
              const posts = Array.isArray(data.initiatedPosts)
                ? data.initiatedPosts
                : [];

              if (posts.length > 0) {
                settle({ posts });
                return;
              }

              if (data.status === "failed") {
                settle({ posts: [], status: "failed" });
                return;
              }

              if (data.status === "empty") {
                settle({ posts: [], status: "empty" });
              }
            },
            (error) => {
              console.error("Failed to watch generation request:", error);
            },
          );
        } catch (error) {
          console.error("Failed to watch generation request:", error);
        }
      });
    },
    [currentUser, activeWorkspaceId],
  );

  const recoverAmbiguousGenerationStart = useCallback(
    async (requestId: string) => {
      if (!activeWorkspaceId) return false;
      const confirmation = await waitForInitiatedPosts(requestId);
      if (confirmation.posts.length > 0) {
        if (!embedded && sourcesStorageKey) sessionStorage.removeItem(sourcesStorageKey);
        if (!embedded && smartBarStorageKey) sessionStorage.removeItem(smartBarStorageKey);
        updatePendingRequest(activeWorkspaceId, requestId, {
          postIds: confirmation.posts.map((post) => post.postId),
          confirming: false, error: undefined,
        });
        // The normal deferred flow is already on the gallery. Add the
        // generated-count query param there, but never pull someone back if
        // they navigated elsewhere while Phase A was still running.
        if (embedded || window.location.pathname === "/generate") {
          finishGenerationHandoff(confirmation.posts);
        }
        return true;
      }

      if (confirmation.status === "timeout") {
        // The server may still commit this request. Keep its pending marker and
        // avoid offering a fresh submission that could create duplicate posts.
        const knownRequest = readPendingRequests(activeWorkspaceId).find((item) => item.clientRequestId === requestId);
        // The gallery may have recovered its listener before this waiter did.
        // Never overwrite a confirmed failure or an accepted post list.
        if (!knownRequest?.error && !knownRequest?.postIds?.length) {
          updatePendingRequest(activeWorkspaceId, requestId, { confirming: true });
        }
        if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
        return false;
      }
      if (!embedded) removePendingRequest(activeWorkspaceId, requestId);

      const description =
        confirmation.status === "empty"
          ? "We couldn't find enough usable content to make posts. Try another source."
          : "No draft was created. Please try again in a moment.";

      recordFailure(description, undefined, requestId);

      if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
      return false;
    },
    [
      activeWorkspaceId,
      embedded,
      finishGenerationHandoff,
      smartBarStorageKey,
      sourcesStorageKey,
      toast,
      waitForInitiatedPosts,
      recordFailure,
    ],
  );

  const showGenerationError = ({ title, description }: {
    title: string; description?: string; variant?: "destructive"; duration?: number;
  }) => recordFailure(description || title, title);

  const handleGenerate = async () => {
    if (generationInFlightRef.current) return;

    const validSources = sources.filter((s) => s.value.trim());

    if (!currentUser || !activeWorkspaceId || validSources.length === 0) {
      showGenerationError({
        variant: "destructive",
        title: "Missing Information",
        description: "Please add at least one source.",
      });
      return;
    }

    if (imageStyleUnavailableReason) {
      showGenerationError({
        title: "Images needed",
        description: imageStyleUnavailableReason,
      });
      return;
    }

    // Text validation
    const textSources = validSources.filter((s) => s.type === "text");
    for (const ts of textSources) {
      if (ts.value.trim().length < 100) {
        showGenerationError({
          variant: "destructive",
          title: "Text Too Short",
          description: "Text sources must be at least 100 characters.",
        });
        return;
      }
      if (ts.value.trim().length > 100000) {
        showGenerationError({
          variant: "destructive",
          title: "Text Too Long",
          description: "Text sources must be under 100,000 characters.",
        });
        return;
      }
    }

    // Note: videoCredits === 0 is handled up-front via disabled Generate button
    // + inline upgrade prompt. Still guard here so backend calls aren't wasted
    // if state somehow slips through (race condition on credit deduction).
    if (videoCredits === 0) {
      handleGetMoreCredits();
      return;
    }

    // Posts per source cap validation
    let maxPostsPerSource: number | undefined;
    if (postsPerSourceCap.trim()) {
      const parsed = parseInt(postsPerSourceCap, 10);
      if (isNaN(parsed) || parsed < 1 || parsed > 10) {
        showGenerationError({
          variant: "destructive",
          title: "Invalid Limit",
          description:
            "Please enter a number between 1 and 10 for the per-source limit.",
        });
        return;
      }
      maxPostsPerSource = parsed;
    }

    generationInFlightRef.current = true;
    const generationClickedAt = Date.now();
    const clientRequestId = crypto.randomUUID();
    const reportRequestFailure = (message: string) => recordFailure(message, undefined, clientRequestId);
    // Resolve once per generation and carry the assignment through Phase A
    // and every Cloud Task. This keeps all posts in one run in one cohort.
    const generationQualityVariant = getGenerationQualityVariant(
      selectedVisualStyleId,
    );
    let requestSent = false;
    clearConfigurationError();
    setHandoff(null);
    setIsGenerating(true);
    // Attempt marker: post docs only exist on success, so without this a
    // user who tries and abandons/fails is indistinguishable from one who
    // never tried (the gap that made the 2026-07 funnel diagnosis need
    // Firestore + dead-click archaeology).
    posthog.capture("generation_started", {
      client_request_id: clientRequestId,
      validation_ids: validSources.flatMap(source => source.validationId ? [source.validationId] : []),
      handoff_ids: validSources.flatMap(source => source.documentId ? [source.documentId] : []),
      entry_point: "source_input",
      source_count: validSources.length,
      source_types: validSources.map((s) => s.documentId ? "pdf_upload" : s.kind || s.type),
      mode: validSources.length > 1 ? generationMode : "batch",
      generation_quality_variant: generationQualityVariant,
      visual_style_id: selectedVisualStyleId,
    });
    // Record the request before the handoff. The gallery immediately renders
    // its source shelf from this marker while Phase A creates placeholder
    // posts. Persisted inputs are cleared only after the server commits the
    // request, so a failure can still be retried from the Generate page.
    const sourceMetadata: PendingSource[] = validSources.map((s, index) => {
      const sourceId = s.id || `source-${index}`;
      if (s.type === "text") {
        return {
          sourceId,
          type: "text",
          label: s.title || "Pasted text",
          generationSource: "direct-text-input",
        };
      }
      if (s.kind === "youtube" || isYoutubeUrlValue(s.value)) {
        return {
          sourceId,
          type: "youtube",
          label: s.title || "YouTube video",
          generationSource: s.value.trim(),
        };
      }
      let label = s.title?.trim() || "";
      if (!label) {
        try {
          label = new URL(
            /^https?:\/\//i.test(s.value) ? s.value : `https://${s.value}`,
          ).hostname.replace(/^www\./, "");
        } catch {
          label = "Link";
        }
      }
      return {
        sourceId,
        type: s.documentId ? "pdf" : "url",
        label,
        favicon: s.favicon,
        generationSource: s.value.trim(),
      };
    });
    let pendingSources = sourceMetadata;

    // Synthesis deliberately produces one set of posts from the combined
    // material, so it is one source row rather than several rows that could
    // never be joined to the combined generationSource stored on each post.
    if (validSources.length > 1 && generationMode === "synthesis") {
      pendingSources = [
        {
          sourceId: "synthesis",
          type: "url",
          label: `${validSources.length} sources combined`,
          generationSource: validSources
            .map((s) => (s.type === "url" ? s.value.trim() : "text-input"))
            .join(" + "),
        },
      ];
    }
    appendPendingRequest(activeWorkspaceId, {
      retryData: { ...captureComposer(), sources: validSources },
      clientRequestId,
      clickedAt: generationClickedAt,
      sources: pendingSources,
      maxPostsPerSource,
      generationQualityVariant,
    });
    const clearPendingMarker = () => {
      if (!embedded) removePendingRequest(activeWorkspaceId, clientRequestId);
    };

    if (retryingRequestId.current) {
      removePendingRequest(activeWorkspaceId, retryingRequestId.current);
      retryingRequestId.current = null;
    }
    if (embedded) {
      // An overlay opens Drafts once it has closed; an inline page goes now.
      openDraftsAfterClose.current = !inline;
      configurationHasClosed.current = inline;
      if (parkedComposer.current) {
        restoreComposer(parkedComposer.current.config); setSmartBarValue(parkedComposer.current.input);
        parkedComposer.current = null;
      } else { setSources([]); setSmartBarValue(""); }
      setSmartBarFocused(false); setSmartBarError(""); setConfigurationOpen(false);
      if (inline) {
        if (onSubmitted) onSubmitted();
        else navigate("/created", { state: { fromGeneration: true } });
      }
    } else navigate("/created", { state: { fromGeneration: true } });

    try {
      // Text read from an Instagram profile is material, never words to keep.
      const isSingleTextSource =
        validSources.length === 1 && validSources[0].type === "text" && !validSources[0].fromLink;

      if (validSources.some((source) => isLocalFailureInput(source.value))) {
        await new Promise((resolve) => window.setTimeout(resolve, 800));
        throw new Error("Local failure preview. No generation request was sent and no credits were used. Remove the local failure marker before making a real request.");
      }
      const token = await currentUser.getIdToken();
      requestSent = true;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/article/initiate-generation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Idempotency-Key": clientRequestId,
          },
          body: JSON.stringify({
            clientRequestId,
            sources: validSources.map((s, index) => ({
              sourceId: sourceMetadata[index]?.sourceId,
              documentId: s.documentId,
              type: s.type,
              value: s.value.trim(),
              label: sourceMetadata[index]?.label,
              favicon: sourceMetadata[index]?.favicon,
              kind: s.kind,
            })),
            mode: validSources.length > 1 ? generationMode : "batch",
            contentIntent: instructions.trim() || undefined,
            maxPostsPerSource,
            preserveInputTextAsCaption: isSingleTextSource
              ? preserveInputAsCaption
              : undefined,
            brandKitId: selectedBrandKitId,
            outputOptions: ["carousel"],
            chosenTemplateIds:
              selectedTemplateIds.size > 0
                ? Array.from(selectedTemplateIds)
                : undefined,
            targetLanguage:
              targetLanguage === "auto" ? undefined : targetLanguage,
            visualStyleId: selectedVisualStyleId,
            generationQualityVariant,
          }),
        },
      );

      const responseContentType = response.headers.get("content-type") || "";
      const responseText = await response.text();
      let result: any = {};
      if (responseText) {
        try {
          result = JSON.parse(responseText);
        } catch {
          result = {
            error: response.statusText || "Generation Failed",
            details: responseText.slice(0, 300),
          };
        }
      }

      if (!response.ok) {
        if (response.status === 402) {
          if (embedded) {
            updatePendingRequest(activeWorkspaceId, clientRequestId, {
              error: "Not enough credits to start this request.",
              errorCode: "insufficient_credits",
            });
          } else {
            reportRequestFailure("Not enough credits to start this request.");
          }
          clearPendingMarker();
          if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
          if (!embedded) handleGetMoreCredits();
          return;
        }

        if (
          [502, 503, 504].includes(response.status) &&
          !responseContentType.includes("application/json")
        ) {
          await recoverAmbiguousGenerationStart(clientRequestId);
          return;
        }

        const errorTitle = result.error || "Generation Failed";
        let errorDescription =
          result.details || "Failed to initiate post generation.";

        if (result.suggestion) {
          errorDescription += `\n\n\u{1F4A1} ${result.suggestion}`;
        }

        if (!embedded && result.fallback?.type === "paste_transcript" && result.url) {
          setSources((prev) =>
            prev.map((source) =>
              source.type === "url" && source.value === result.url
                ? {
                    ...source,
                    error:
                      "We couldn't import this video. Paste the transcript or notes as a text source instead.",
                  }
                : source,
            ),
          );
        }

        if (!embedded && result.fallback?.type === "paste_text" && result.url) {
          const failedUrl = result.url;
          setSources((prev) =>
            prev.map((source) =>
              source.type === "url" && source.value === failedUrl
                ? {
                    ...source,
                    error:
                      result.error ||
                      "We couldn't read this page. Paste its text as a source instead.",
                  }
                : source,
            ),
          );

        }

        reportRequestFailure(errorDescription);
        updatePendingRequest(activeWorkspaceId, clientRequestId, generationFailurePatch(result));
        if (!embedded) showGenerationError({
          variant: "destructive",
          title: errorTitle,
          description: errorDescription,
          duration: 10000,
        });
        if (!embedded && result.url && ["paste_text", "paste_transcript"].includes(result.fallback?.type)) {
          setFailureFallback({ url: result.url, label: result.fallback.type === "paste_transcript" ? "Paste transcript instead" : "Paste text instead" });
        }
        clearPendingMarker();
        if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
        return;
      }

      if (result.inProgress) {
        await recoverAmbiguousGenerationStart(clientRequestId);
        return;
      }

      if (result.initiatedPosts && result.initiatedPosts.length > 0) {
        // Click → server response: how long Phase A held the request.
        posthog.capture("generation_request_returned", {
          client_request_id: clientRequestId,
          entry_point: "source_input",
          duration_ms: Date.now() - generationClickedAt,
          source_count: validSources.length,
          post_count: result.initiatedPosts.length,
          generation_quality_variant: generationQualityVariant,
          visual_style_id: selectedVisualStyleId,
        });
        // Generation is committed — safe to drop the restorable inputs.
        if (!embedded && sourcesStorageKey) sessionStorage.removeItem(sourcesStorageKey);
        if (!embedded && smartBarStorageKey) sessionStorage.removeItem(smartBarStorageKey);
        // Add the concrete post ids so the gallery can measure request
        // completion and the first-to-last straggler gap.
        updatePendingRequest(activeWorkspaceId, clientRequestId, {
          postIds: result.initiatedPosts.map((p: InitiatedPost) => p.postId),
        });
        // Add the free-plan generated count without pulling someone back to
        // the gallery if they moved elsewhere during Phase A.
        if (embedded || window.location.pathname === "/generate") {
          finishGenerationHandoff(result.initiatedPosts);
        }
      } else {
        clearPendingMarker();
        reportRequestFailure(result.message || "No suitable topics found from the provided sources.");
        if (!embedded) showGenerationError({
          title: "No Posts to Generate",
          description:
            result.message ||
            "No suitable topics found from the provided sources.",
          duration: 7000,
        });
        if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
      }
    } catch (error: any) {
      console.error("Error generating posts:", error);

      const errorMessageText =
        typeof error?.message === "string" ? error.message : "";
      const browserOffline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      const mightHaveStarted =
        requestSent &&
        !browserOffline &&
        (error instanceof TypeError ||
          /Load failed|Failed to fetch|NetworkError|fetch/i.test(
            errorMessageText,
          ));

      if (mightHaveStarted) {
        await recoverAmbiguousGenerationStart(clientRequestId);
        return;
      }

      let errorMessage = "An unexpected error occurred. ";
      if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Please check your internet connection and try again.";
      }

      reportRequestFailure(errorMessage);
      if (!embedded) showGenerationError({
        variant: "destructive",
        title: "Generation Failed",
        description: errorMessage,
        duration: 8000,
      });
      clearPendingMarker();
      if (!embedded) { generationInFlightRef.current = false; setIsGenerating(false); }
    }
  };

  const atSourceLimit = sources.length >= 10;
  const showCombineQuestion = sources.length > 1;
  // Text read from an Instagram profile is material, never a caption to keep,
  // whatever was chosen for earlier pasted text.
  const showCaptionQuestion =
    sources.length === 1 && sources[0].type === "text" && !sources[0].fromLink;
  const keepsCaption = preserveInputAsCaption && !sources.some((source) => source.fromLink);

  const canGenerate =
    sources.length > 0 &&
    !imageStyleUnavailableReason &&
    !sources.some((s) => !!s.error) &&
    !isGenerating &&
    !isValidatingUrls &&
    videoCredits > 0;

  // "Make my posts" adds what's still in the input and generates in one step,
  // so the estimate (and its "edit cap") counts that content already — and the
  // button only turns on for input the estimate counts, so they appear together.
  const existingUrlValues = new Set(
    sources.filter((s) => s.type === "url").map((s) => s.value),
  );
  const pendingSourceCount =
    !inline || !smartBarValue.trim()
      ? 0
      : isAllUrls
        ? smartBarLines.filter((l) => !existingUrlValues.has(withLinkScheme(l))).length
        : smartBarSplit
          ? smartBarSplit.links.filter((l) => !existingUrlValues.has(l)).length
          : smartBarValue.trim().length >= 100
          ? 1
          : 0;
  const estimateSourceCount = Math.min(10, sources.length + pendingSourceCount);

  // The inline page has no separate "add" step: typed or pasted content is
  // added as a source first, then generation starts once it has been accepted.
  const hasPendingInput =
    inline && !!smartBarValue.trim() && !isGenerating && !isValidatingUrls && videoCredits > 0;
  const canSubmitInput = hasPendingInput && pendingSourceCount > 0;
  const generateWhenAdded = useRef<Source[] | null>(null);
  const submitComposer = async () => {
    if (!hasPendingInput) {
      void handleGenerate();
      return;
    }
    generateWhenAdded.current = sources;
    const added = await submitSmartBar();
    if (!added.length) generateWhenAdded.current = null;
  };
  useEffect(() => {
    const before = generateWhenAdded.current;
    if (!before || sources === before || isValidatingUrls) return;
    generateWhenAdded.current = null;
    if (canGenerate) void handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, isValidatingUrls]);

  // Global Cmd/Ctrl+Enter triggers Generate — but let the smart bar textarea
  // keep its own Cmd+Enter (for multi-line submit) when focused.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || (!e.metaKey && !e.ctrlKey)) return;
      if (document.activeElement === textAreaRef.current) return;
      if (!canGenerate || (embedded && !configurationOpen)) return;
      e.preventDefault();
      handleGenerate();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canGenerate, embedded, configurationOpen, handleGenerate]);

  const exampleInputs = (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-2 text-xs text-muted-foreground sm:gap-x-3">
      <span className="sm:hidden">Try:</span>
      <span className="hidden sm:inline">Try an example:</span>
      {[
        { label: "Article URL", value: EXAMPLE_URL },
        {
          label: "YouTube",
          value: "https://www.youtube.com/watch?v=UF8uR6Z6KLc",
        },
        {
          label: "Google Doc",
          value:
            "https://docs.google.com/document/d/1cki5nA3120ffpOVwHJUamaxbGhrsEOX9ERQ_6rTiHL8/edit",
        },
        {
          label: "Pasted text",
          value:
            "Building a consistent content habit starts with noticing useful lessons in your everyday work. Write down one lesson each day, collect a concrete example, and explain how someone else can apply it. At the end of the week, choose the strongest idea and turn it into a short post. Consistency comes from a repeatable process, not from waiting for inspiration.",
        },
      ].map((example) => (
        <button
          key={example.label}
          type="button"
          data-source-action
          className="underline underline-offset-4 hover:text-foreground"
          onClick={() => {
            inputEdited.current = true;
            setSmartBarValue(example.value);
            setSmartBarError("");
            textAreaRef.current?.focus();
          }}
        >
          {example.label}
        </button>
      ))}
    </div>
  );

  const generator = (
    <div className="min-h-full relative">
      <div
        className={cn(
          embedded
            ? configurationOpen
              ? "mx-auto max-w-2xl transition-opacity duration-300"
              : "transition-opacity duration-300"
            : "max-w-2xl mx-auto px-6 py-12 md:py-16 transition-opacity duration-300",
          isGenerating && !embedded && "opacity-30 pointer-events-none select-none",
        )}
        aria-hidden={isGenerating && !embedded ? "true" : undefined}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <fieldset disabled={isGenerating && (!embedded || configurationOpen)} className="min-w-0">
          {/* ── Tier 1: Hero ─────────────────────────────────────── */}
          {!embedded && (
            <header className="mb-10 text-center">
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
                Turn writings into social posts.
              </h1>
              <p className="mt-3 text-base text-muted-foreground">
                Carousels and videos in about 90 seconds.
              </p>
              <a
                href="/generate"
                className="mt-4 inline-block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Try the new Studio preview →
              </a>
            </header>
          )}

          {handoff && <div role={handoff.status === "error" ? "alert" : "status"} className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
            {handoff.status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /><span>Restoring your input…</span></> : <>
              <p className="w-full">{handoff.message}</p>
              <Button type="button" variant="outline" size="sm" onClick={() => setHandoffAttempt(attempt => attempt + 1)}>Try restoring again</Button>
              <a href={`${process.env.REACT_APP_WEBSITE_URL || "https://wavegen.ai"}/pdf-to-carousel`} className="font-medium underline underline-offset-4">Upload another PDF</a>
            </>}
          </div>}

          {/* ── Sources section ─────────────────────────────────────── */}
          {sources.length > 0 && (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {sourceHeading || (sources.length === 1 ? "Source added" : "Sources added")}
                </p>
                {!sourceHeading && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {sources.length}/10
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {sources.map((source) => {
                  const video = source.kind === "youtube";
                  // Uploaded PDFs are stored behind an internal link nobody needs to read.
                  const pdf = !!source.documentId || source.kind === "pdf";
                  return (
                    <SourceCard
                      key={source.id}
                      label={source.title || (source.type === "url" ? displayLink(source.value) : "Pasted text")}
                      icon={source.type === "text" || pdf ? FileText : LinkIcon}
                      favicon={source.type === "url" && !pdf ? source.favicon : undefined}
                      thumbnailUrl={video ? source.thumbnailUrl : undefined}
                      quote={source.type === "text" ? source.value : undefined}
                      title={source.type === "url" ? source.title || displayLink(source.value) : undefined}
                      details={source.type !== "url" ? (source.fromLink && source.title ? [source.title] : []) : video
                        ? [[source.channel, formatVideoDuration(source.durationSec)].filter(Boolean).join(" · ") || displayLink(source.value)]
                        : source.title && !source.documentId ? [displayLink(source.value)] : []}
                      status={source.error ? "error" : "ok"}
                      note={source.error && (
                        <p className="mt-0.5 text-xs text-destructive">{source.error}</p>
                      )}
                      onEdit={source.type === "text" ? () => editTextSource(source) : undefined}
                      action={
                        <button
                          type="button"
                          onClick={() => removeSource(source.id)}
                          className="shrink-0 self-start rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label="Remove source"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      }
                    >
                      {/* Inline caption question — only for a single text source */}
                      {showCaptionQuestion && source.type === "text" && (
                        <>
                          <p className="text-xs text-muted-foreground mb-2">
                            Keep this text as your caption?
                          </p>
                          <ToggleGroup
                            type="single"
                            size="sm"
                            variant="outline"
                            value={
                              preserveInputAsCaption ? "preserve" : "repurpose"
                            }
                            onValueChange={(v) => {
                              if (v) setPreserveInputAsCaption(v === "preserve");
                            }}
                            className="shadow-none"
                          >
                            <ToggleGroupItem value="repurpose" className="px-3">
                              Let AI rewrite
                            </ToggleGroupItem>
                            <ToggleGroupItem value="preserve" className="px-3">
                              Use my words
                            </ToggleGroupItem>
                          </ToggleGroup>
                        </>
                      )}
                    </SourceCard>
                  );
                })}
                {atSourceLimit && (
                  <p className="text-xs text-muted-foreground">
                    Maximum 10 sources reached.
                  </p>
                )}
              </div>

              {/* Contextual combine question (2+ sources) */}
              {showCombineQuestion && (
                <div className="rounded-md bg-muted/30 p-4 space-y-3">
                  <p className="text-sm text-foreground">
                    How should we combine these {sources.length} sources?
                  </p>
                  <ToggleGroup
                    type="single"
                    size="sm"
                    variant="outline"
                    value={generationMode}
                    onValueChange={(v) => {
                      if (v) setGenerationMode(v as "batch" | "synthesis");
                    }}
                    className="shadow-none"
                  >
                    <ToggleGroupItem value="batch" className="px-3">
                      Separate posts for each
                    </ToggleGroupItem>
                    <ToggleGroupItem value="synthesis" className="px-3">
                      One roundup post
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}

              {/* One place to say what you want, for any number of sources.
                  Sent as contentIntent; the slide writer follows design asks too. */}
              <div className="space-y-1.5">
                <label
                  htmlFor="generate-instructions"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Any instructions? <span className="font-normal">(optional)</span>
                </label>
                <Input
                  id="generate-instructions"
                  placeholder="e.g. focus on pricing, keep it casual, dark background"
                  value={instructions}
                  maxLength={500}
                  onChange={(e) => setInstructions(e.target.value.slice(0, 500))}
                />
              </div>
            </section>
          )}

          {sources.length > 0 && !atSourceLimit && !addingSource && !smartBarValue.trim() && !smartBarError && (
            <Button
              ref={addSourceButtonRef}
              type="button"
              variant="ghost"
              size="sm"
              className="mt-3 -ml-2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setAddingSource(true);
                requestAnimationFrame(() => textAreaRef.current?.focus());
              }}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Add another source <span className="ml-1 font-normal">(optional)</span>
            </Button>
          )}

          {pdfRecovery && <PdfRecoveryNotice />}

          {/* ── Smart Bar ─────────────────────────────────────── */}
          {!atSourceLimit && (sources.length === 0 || addingSource || smartBarValue.trim() || smartBarError) && (
            <div className={sources.length > 0 ? "mt-4" : undefined}>
              {/* Backdrop: dims the rest of the page when smart bar is focused */}
              {smartBarFocused && !(embedded && !configurationOpen) && (
                <div
                  className="fixed inset-0 bg-black/5 z-10"
                  // Clicking here blurs the box, which accepts its content.
                  onClick={() => setSmartBarFocused(false)}
                />
              )}

              <div
                ref={smartBarRef}
                onFocus={() => setSmartBarFocused(true)}
                onBlur={(event) => {
                  // Moving from the input to Add source must keep the action
                  // mounted until its click (or keyboard activation) runs.
                  const next = event.relatedTarget as HTMLElement | null;
                  if (event.currentTarget.contains(next)) return;
                  setSmartBarFocused(false);
                  // Controls marked data-source-action (Make my posts, examples,
                  // the overlay's Continue) take the input themselves.
                  if (!configurationOpen || next?.closest("[data-source-action]")) return;
                  // Wait a tick: switching windows also blurs, and then the
                  // page no longer has focus — that isn't leaving the box.
                  window.setTimeout(() => {
                    if (!document.hasFocus()) return;
                    if (smartBarRef.current?.contains(document.activeElement)) return;
                    acceptOnLeave();
                  });
                }}
                className={cn(
                  "transition-all",
                  smartBarFocused && "relative z-20",
                )}
              >
                <div
                  className={cn(
                    "rounded-lg border bg-background overflow-hidden transition-all",
                    smartBarFocused ? "ring-2 ring-ring shadow-lg" : "",
                    smartBarError
                      ? "border-destructive/40 ring-destructive/50"
                      : "border-input",
                  )}
                >
                  <textarea
                    ref={textAreaRef}
                    id={embedded ? "studio-source-input" : undefined}
                    placeholder={
                      sources.length === 0
                        ? embedded && !configurationOpen
                          ? "Paste an article URL, YouTube link, Google Doc URL, or text."
                          : "Paste an article URL, YouTube link, Google Doc link, or text"
                        : "Paste another URL or text"
                    }
                    value={smartBarValue}
                    onChange={(e) => {
                      inputEdited.current = true;
                      if (e.target.value.length <= 100000) {
                        setSmartBarValue(e.target.value);
                      }
                      setSmartBarError("");
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !e.nativeEvent.isComposing &&
                        !e.shiftKey &&
                        ((embedded && !configurationOpen) || !isMultiLine || isAllUrls || smartBarSplit) &&
                        smartBarValue.trim()
                      ) {
                        e.preventDefault();
                        submitSmartBar();
                        return;
                      }
                      if (
                        e.key === "Enter" &&
                        (e.metaKey || e.ctrlKey) &&
                        smartBarValue.trim()
                      ) {
                        e.preventDefault();
                        submitSmartBar();
                      }
                      if (e.key === "Escape") {
                        setSmartBarFocused(false);
                        textAreaRef.current?.blur();
                        if (!smartBarValue.trim()) {
                          setAddingSource(false);
                          requestAnimationFrame(() => addSourceButtonRef.current?.focus());
                        }
                      }
                    }}
                    disabled={isValidatingUrls}
                    aria-label="Content to generate from"
                    rows={embedded && !configurationOpen ? 3 : 1}
                    className={cn(
                      "w-full resize-none bg-transparent px-3 text-sm",
                      "placeholder:text-muted-foreground focus:outline-none",
                      "disabled:opacity-50",
                      smartBarFocused ? "py-3" : "py-3",
                    )}
                    style={{
                      minHeight:
                        (embedded && !configurationOpen) || smartBarFocused
                          ? "120px"
                          : "48px",
                      overflow: "hidden",
                    }}
                  />

                  {(smartBarFocused || isValidatingUrls || sources.length > 0) && (
                    <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
                      <div className="flex items-center gap-3">
                        {!isAllUrls &&
                          !smartBarSplit &&
                          smartBarValue.length > 0 &&
                          (smartBarValue.length < 100 ? (
                            <p className="text-xs text-muted-foreground">
                              Add at least {100 - smartBarValue.length} more
                              character
                              {100 - smartBarValue.length !== 1 ? "s" : ""}
                            </p>
                          ) : (
                            <p
                              className={cn(
                                "text-xs",
                                smartBarValue.length >= 100000
                                  ? "text-destructive"
                                  : "text-muted-foreground",
                              )}
                            >
                              {smartBarValue.length.toLocaleString()} / 100,000
                            </p>
                          ))}
                        {isAllUrls && smartBarLines.length > 1 && (
                          <p className="text-xs text-muted-foreground">
                            {smartBarLines.length} URLs
                          </p>
                        )}
                        {!smartBarValue && (
                          <p className="text-xs text-muted-foreground">
                            {embedded && !configurationOpen ? "Press Enter to continue." : "Press Enter to add."}
                          </p>
                        )}
                      </div>
                      {isValidatingUrls ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Validating...
                        </div>
                      ) : !(embedded && !configurationOpen) ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => submitSmartBar()}
                          disabled={!smartBarValue.trim()}
                        >
                          Add source
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>

                {smartBarError && (
                  <p className="mt-1.5 text-xs text-destructive">
                    {smartBarError}
                  </p>
                )}
              </div>

              {!embedded &&
                sources.length === 0 &&
                !smartBarValue &&
                !smartBarFocused && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Not sure what to paste?{" "}
                    <button
                      type="button"
                      onClick={() => submitSmartBar(EXAMPLE_URL)}
                      className="text-brand underline hover:text-brand/80 transition-colors"
                    >
                      Try an example →
                    </button>
                  </p>
                )}
            </div>
          )}

          {embedded && !configurationOpen && selectedTemplateIds.size > 0 && (
            <button
              type="button"
              data-source-action
              disabled={isGenerating}
              onClick={() => { if (!generationInFlightRef.current) setConfigurationOpen(true); }}
              className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-haspopup="dialog"
            >
              {selectedTemplateLabel ? `Template: ${selectedTemplateLabel}` : "Template selected"}
            </button>
          )}

          {inline && sources.length === 0 && (
            <div className="mt-4">{exampleInputs}</div>
          )}

          {embedded && !configurationOpen && (
            <div className="mt-4 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
              {exampleInputs}
              <Button
                type="button"
                data-source-action
                onPointerDown={(event) => {
                  // Keep the input footer from collapsing and moving this button
                  // between pointer-down and click (including unfocusable buttons).
                  if (event.button === 0) event.preventDefault();
                }}
                variant={configurationOpen ? "ghost" : "default"}
                className={
                  configurationOpen
                    ? ""
                    : "bg-brand text-brand-foreground hover:bg-brand/90"
                }
                disabled={
                  isValidatingUrls ||
                  isGenerating ||
                  (!configurationOpen &&
                    !sources.length &&
                    !smartBarValue.trim())
                }
                aria-expanded={configurationOpen}
                aria-controls="generation-configuration"
                onClick={async () => {
                  if (configurationOpen) {
                    setConfigurationOpen(false);
                    return;
                  }
                  if (smartBarValue.trim()) {
                    const added = await submitSmartBar();
                    if (!added.length) return;
                  }
                  setConfigurationOpen(true);
                }}
              >
                {configurationOpen
                  ? "Hide configuration"
                  : isValidatingUrls
                    ? "Checking source…"
                    : "Make my posts"}
              </Button>
            </div>
          )}
          <div
            id="generation-configuration"
            hidden={embedded && !configurationOpen}
          >
            {/* ── Tier 2: Settings ─────────────────────────────────────── */}
            <GenerationSettings
              userBrandKits={userBrandKits}
              sourceMayHaveImages={sourceMayHaveImages}
              selectedBrandKitId={selectedBrandKitId}
              setSelectedBrandKitId={setSelectedBrandKitId}
              selectedVisualStyleId={selectedVisualStyleId}
              setSelectedVisualStyleId={setSelectedVisualStyleId}
              targetLanguage={targetLanguage}
              setTargetLanguage={setTargetLanguage}
              // Accounts are chosen when publishing, not when generating.
              showPostingTo={false}
              showManageBrandKits={showManageBrandKits}
              brandKitPending={!!selectedBrandKitId && !selectedBrandKit}
              noBrandKitNote={
                !showManageBrandKits && brandKitsLoaded && !selectedBrandKitId
                  ? "You can add a brand kit in your workspace."
                  : undefined
              }
              selectedTemplateIds={selectedTemplateIds}
              setSelectedTemplateIds={setSelectedTemplateIds}
              setSelectedTemplateLabel={setSelectedTemplateLabel}
            />

            <hr className="my-10 border-border/60" />

            {/* ── Tier 3: Estimate + Generate ─────────────────────────── */}
            <div className="space-y-3">
              {estimateSourceCount > 0 &&
                (() => {
                  const effectiveMode =
                    estimateSourceCount > 1 ? generationMode : "batch";
                  const isBatch =
                    effectiveMode === "batch" && !keepsCaption;
                  const perSource = postsPerSourceCap.trim()
                    ? parseInt(postsPerSourceCap, 10) || 5
                    : 5;
                  const isVisualForText =
                    estimateSourceCount === 1 &&
                    (sources.length
                      ? sources[0].type === "text"
                      : !isAllUrls && !smartBarSplit) &&
                    keepsCaption;
                  const maxEstimate = isVisualForText
                    ? 1
                    : isBatch
                      ? Math.min(videoCredits, estimateSourceCount * perSource)
                      : Math.min(videoCredits, 5);
                  const minEstimate = isVisualForText
                    ? 1
                    : isBatch
                      ? Math.min(videoCredits, estimateSourceCount)
                      : 1;
                  const notEnough = videoCredits < estimateSourceCount;

                  return (
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap items-baseline gap-x-1 text-muted-foreground">
                        <span>
                          {estimateSourceCount} source
                          {estimateSourceCount !== 1 ? "s" : ""}
                        </span>
                        <span>&middot;</span>
                        <span className="font-medium text-foreground">
                          {minEstimate === maxEstimate
                            ? `${maxEstimate} post${maxEstimate !== 1 ? "s" : ""}`
                            : `${minEstimate}\u2013${maxEstimate} posts`}
                        </span>
                        <span>&middot;</span>
                        <span>
                          {videoCredits} credit
                          {videoCredits !== 1 ? "s" : ""} available
                        </span>
                        {isBatch && !showPostsCap && !postsPerSource && (
                          <>
                            <span>&middot;</span>
                            <button
                              type="button"
                              onClick={() => setShowPostsCap(true)}
                              className="underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              edit cap
                            </button>
                          </>
                        )}
                      </div>

                      {showPostsCap && isBatch && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            Limit to
                          </span>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            placeholder="5"
                            value={postsPerSourceCap}
                            onChange={(e) =>
                              setPostsPerSourceCap(e.target.value)
                            }
                            className="w-14 rounded border border-input bg-background px-2 py-0.5 text-sm text-center text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                          />
                          <span className="text-muted-foreground">
                            posts per source
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setPostsPerSourceCap("");
                              setShowPostsCap(false);
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Clear
                          </button>
                        </div>
                      )}

                      {notEnough && (
                        <p className="text-brand">
                          Not enough credits for all sources (need at least{" "}
                          {estimateSourceCount}).{" "}
                          {smallestCreditPack && (
                            <>
                              <button
                                type="button"
                                onClick={handleGetMoreCredits}
                                className="underline hover:text-brand/80 transition-colors"
                              >
                                Get more credits
                              </button>
                              {", "}
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              navigateToUpgrade({ reason: "no_credits" })
                            }
                            className="underline hover:text-brand/80 transition-colors"
                          >
                            upgrade
                          </button>
                          , or remove some sources.
                        </p>
                      )}
                    </div>
                  );
                })()}
              {!failureMessage && sources.length > 0 && sources.some((s) => !!s.error) && (
                <p className="text-sm text-destructive">
                  {sources.filter((s) => !!s.error).length} source
                  {sources.filter((s) => !!s.error).length > 1
                    ? "s have"
                    : " has"}{" "}
                  an error. Fix or remove{" "}
                  {sources.filter((s) => !!s.error).length > 1 ? "them" : "it"}{" "}
                  to continue.
                </p>
              )}
              {sources.length > 0 && videoCredits === 0 && (
                <p className="text-sm text-muted-foreground">
                  You're out of credits.{" "}
                  {smallestCreditPack && (
                    <>
                      <button
                        type="button"
                        onClick={handleGetMoreCredits}
                        className="text-brand underline hover:text-brand/80 transition-colors"
                      >
                        Get more credits
                      </button>{" "}
                      or{" "}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => navigateToUpgrade({ reason: "no_credits" })}
                    className="text-brand underline hover:text-brand/80 transition-colors"
                  >
                    upgrade
                  </button>{" "}
                  to continue.
                </p>
              )}
              {failureMessage && (
                <div role="alert" className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm">
                  <p className="font-medium">{failureTitle}</p>
                  <p className="mt-1 whitespace-pre-line text-muted-foreground">{failureMessage}</p>
                  {failureFallback && (
                    <button
                      type="button"
                      className="mt-2 font-medium underline underline-offset-4"
                      onClick={() => {
                        setSources((prev) => prev.filter((source) => !(source.type === "url" && source.value === failureFallback.url)));
                        setFailureMessage("");
                        setFailureFallback(null);
                        setSmartBarFocused(true);
                        requestAnimationFrame(() => textAreaRef.current?.focus());
                      }}
                    >
                      {failureFallback.label}
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center justify-end pt-2">
                <Button
                  data-source-action
                  onClick={() => void submitComposer()}
                  disabled={!canGenerate && !canSubmitInput}
                  size="lg"
                  className="gap-3 bg-brand text-brand-foreground hover:bg-brand/90"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting your generation…
                    </>
                  ) : (
                    <>
                      {submitLabel}
                      <span className="hidden sm:inline-flex items-center gap-0.5 opacity-70 text-[0.7rem] font-medium">
                        <kbd className="font-inherit">⌘</kbd>
                        <kbd className="font-inherit">↵</kbd>
                      </span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
          </fieldset>
        </form>
      </div>

      {(isLocalFailureInput(smartBarValue) || sources.some((source) => isLocalFailureInput(source.value))) && (
        <p className="mt-3 text-xs text-muted-foreground">
          Local failure test: URLs are validated normally; generation will then fail locally without using credits.
        </p>
      )}
      {isGenerating && !embedded && (
        <GenerateWorkingState
          allSourcesAreText={allSourcesAreText}
          hasYoutubeSource={hasYoutubeSource}
        />
      )}
    </div>
  );
  if (handoffFailed) return <WebsiteHandoffRecovery error={new WebsiteHandoffError(handoffToolUrl(searchParams))} />;
  return embedded && !inline ? (
    <ExpandingWorkspace
      motion="fade"
      expanded={configurationOpen}
      dismissible={!isGenerating || !configurationOpen}
      onClose={closeConfiguration}
      onAfterClose={() => {
        // The submitted request owns its pending marker. Once the composer
        // closes, the user can start another request while it prepares posts.
        generationInFlightRef.current = false;
        setIsGenerating(false);
        configurationHasClosed.current = true;
        openSubmittedDrafts();
      }}
      title="Generate content"
      description="Configure your inputs and generate posts. Escape returns to Generate."
    >
      {generator}
    </ExpandingWorkspace>
  ) : (
    generator
  );
}
